import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Typography } from '@mui/material';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import ContentArea from '../components/content-area/Content';
import { useToast } from '../context/ToastContext';
import { useNavigationGuard } from '../context/NavigationGuardContext';
import { getCcuSettingsConfig, updateCcuSettings } from '../api/ccuSettingsApi';
import { getAdminEntities } from '../api/paymentApi';
import { computeCcuAllocation } from '../utils/ccuAllocation';
import CcuSettingsForm from '../components/ccu/CcuSettingsForm';
import CcuSpaceTable from '../components/ccu/CcuSpaceTable';
import { C, btnBase, ctaNavy, cardBase } from '../theme/adminTokens';


const SETTINGS_KEYS = [
  'globalMaxNormal',
  'globalMaxAbsolute',
  'maxPlayersPerRoom',
  'inactivityTimeoutSeconds',
  'maxSessionTimerSeconds',
];

function pickSettings(cfg) {
  const out = {};
  SETTINGS_KEYS.forEach((k) => { out[k] = cfg?.[k] ?? 0; });
  return out;
}

function sortedIds(ids) {
  return [...(ids ?? [])].map(Number).sort((a, b) => a - b);
}

function idsEqual(a, b) {
  const sa = sortedIds(a);
  const sb = sortedIds(b);
  return sa.length === sb.length && sa.every((v, i) => v === sb[i]);
}

// ─── Unsaved-changes navigation guard dialog — mirrors AdminPortal.jsx's UnsavedFlagsDialog ──
function UnsavedChangesDialog({ open, saving, onSave, onDiscard, onCancel }) {
  return (
    <Dialog open={open} onClose={onCancel} PaperProps={{ sx: { borderRadius: 3, p: 1 } }} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, color: C.navy }}>Unsaved Changes</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          You have unsaved CCU settings changes. Save them before continuing, or they will be lost.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onCancel} variant="outlined" disabled={saving} sx={{ ...btnBase, flex: 1, fontWeight: 600 }}>Cancel</Button>
        <Button onClick={onDiscard} variant="outlined" disabled={saving} color="error" sx={{ ...btnBase, flex: 1 }}>Discard</Button>
        <Button onClick={onSave} variant="contained" disabled={saving} sx={{ ...btnBase, ...ctaNavy, flex: 1 }}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function CcuSettings() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { registerGuard } = useNavigationGuard();

  const [config, setConfig] = useState(null);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [settings, setSettings] = useState(pickSettings(null));
  const [savedSettings, setSavedSettings] = useState(pickSettings(null));
  const [extraCcuSpaceIds, setExtraCcuSpaceIds] = useState([]);
  const [savedExtraCcuSpaceIds, setSavedExtraCcuSpaceIds] = useState([]);
  const [settingsValid, setSettingsValid] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pendingNavPath, setPendingNavPath] = useState(null);

  const applyConfig = useCallback((cfg) => {
    setConfig(cfg);
    const nextSettings = pickSettings(cfg);
    setSettings(nextSettings);
    setSavedSettings(nextSettings);
    const extraIds = (cfg?.spaces?.extraShareSpaces ?? []).map((s) => s.spaceId);
    setExtraCcuSpaceIds(extraIds);
    setSavedExtraCcuSpaceIds(extraIds);
  }, []);

  // Retryable on its own — both the initial mount and the error banner's "Try Again" button
  // call this. A genuine failure (network/5xx) sets loadError, which replaces the form with
  // a full retry state; a successful-but-empty response (nothing configured yet) is NOT an
  // error — it flows through applyConfig normally and just shows zeroed fields, per the
  // GET-is-honest-about-empty-data design (see CCU_Settings_Migration.sql).
  const fetchConfig = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    getCcuSettingsConfig()
      .then((data) => { if (!cancelled) applyConfig(data); })
      .catch(() => {
        if (!cancelled) {
          toast.error('Failed to load CCU settings');
          setLoadError(true);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyConfig]);

  useEffect(() => fetchConfig(), [fetchConfig]);

  // Entity names for the space table's group headers only — one call, already returns every
  // entity with its nested spaces (same source EntitySpaceFilter.jsx uses), so no per-space
  // follow-up requests are needed. Failure here degrades the table (falls back to "—")
  // rather than blocking the page, since it's not required for load/save.
  useEffect(() => {
    let cancelled = false;
    getAdminEntities()
      .then((data) => { if (!cancelled) setEntities(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setEntities([]); });
    return () => { cancelled = true; };
  }, []);

  const entityNameBySpaceId = useMemo(() => {
    const map = new Map();
    entities.forEach((entity) => {
      (entity.spaces ?? []).forEach((space) => map.set(space.id, entity.name));
    });
    return map;
  }, [entities]);

  // Full non-deleted space list from the GET response (baseShareSpaces + extraShareSpaces
  // are mutually exclusive, so a plain concat is safe) — the table's row source.
  const allSpaces = useMemo(() => {
    if (!config?.spaces) return [];
    return [...(config.spaces.baseShareSpaces ?? []), ...(config.spaces.extraShareSpaces ?? [])]
      .slice()
      .sort((a, b) => a.spaceName.localeCompare(b.spaceName));
  }, [config]);

  // Client-side live preview only (see ccuAllocation.js) — activeSpaceCount is derived
  // server-side and not editable here, so it's read from the loaded space list;
  // extraShareSpaceCount tracks the admin's in-progress selection so the readouts move as
  // they check/uncheck spaces, ahead of the authoritative server recompute on save.
  const computed = useMemo(() => {
    const { baseShareCcu, extraShareCcu, allocatedTotalCcu, normalUnallocatedCcu, absoluteUnallocatedCcu } = computeCcuAllocation({
      globalMaxNormal: settings.globalMaxNormal,
      globalMaxAbsolute: settings.globalMaxAbsolute,
      activeSpaceCount: allSpaces.length,
      extraShareSpaceCount: extraCcuSpaceIds.length,
    });
    return {
      activeSpaceCount: allSpaces.length,
      extraShareSpaceCount: extraCcuSpaceIds.length,
      baseShareCcu,
      extraShareCcu,
      allocatedTotalCcu,
      normalUnallocatedCcu,
      absoluteUnallocatedCcu,
    };
  }, [settings.globalMaxNormal, settings.globalMaxAbsolute, allSpaces.length, extraCcuSpaceIds.length]);

  const isSettingsDirty = SETTINGS_KEYS.some((k) => Number(settings[k]) !== Number(savedSettings[k]));
  const isSpaceSelectionDirty = !idsEqual(extraCcuSpaceIds, savedExtraCcuSpaceIds);
  const isDirty = isSettingsDirty || isSpaceSelectionDirty;

  // Only guards navigation while there's actually something unsaved — registers/unregisters
  // as that condition changes, same idea as AdminPortal.jsx's Feature Flags tab guard.
  useEffect(() => {
    if (!isDirty) return undefined;
    return registerGuard((path) => setPendingNavPath(path));
  }, [isDirty, registerGuard]);

  const handleSave = async () => {
    if (!settingsValid) return false;
    setSaving(true);
    try {
      const saved = await updateCcuSettings({ settings, extraCcuSpaceIds });
      applyConfig(saved);
      toast.success('CCU settings saved');
      return true;
    } catch (_) {
      toast.error('Failed to save CCU settings');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => {
    setSettings(savedSettings);
    setExtraCcuSpaceIds(savedExtraCcuSpaceIds);
  };

  const canSave = isDirty && settingsValid && !saving && !loading;

  return (
    <ContentArea title="CCU Settings" icon={<SpeedOutlinedIcon />}>
      <Paper elevation={0} sx={{ ...cardBase, '&:hover': {}, p: { xs: 2, sm: 3 } }}>
        {loading ? (
          <Typography variant="body2" sx={{ color: C.muted }}>Loading CCU settings…</Typography>
        ) : loadError ? (
          <Box
            sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', minHeight: 280, gap: 2, py: 6,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 800, color: C.navy }}>
              Something went wrong
            </Typography>
            <Typography variant="body2" sx={{ color: C.muted }}>
              Failed to load CCU settings.
            </Typography>
            <Button variant="contained" onClick={fetchConfig} sx={{ ...btnBase, ...ctaNavy }}>
              Try Again
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <CcuSettingsForm
              values={settings}
              onChange={setSettings}
              onValidityChange={setSettingsValid}
              disabled={saving}
            />

            <CcuSpaceTable
              spaces={allSpaces}
              selectedIds={extraCcuSpaceIds}
              onChange={setExtraCcuSpaceIds}
              computed={computed}
              entityNameBySpaceId={entityNameBySpaceId}
              disabled={saving}
            />
          </Box>
        )}
      </Paper>

      {!loadError && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canSave}
            sx={{ ...btnBase, ...ctaNavy }}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </Box>
      )}

      <UnsavedChangesDialog
        open={pendingNavPath !== null}
        saving={saving}
        onCancel={() => setPendingNavPath(null)}
        onDiscard={() => {
          discardChanges();
          if (pendingNavPath) navigate(pendingNavPath);
          setPendingNavPath(null);
        }}
        onSave={async () => {
          const ok = await handleSave();
          if (ok) {
            if (pendingNavPath) navigate(pendingNavPath);
            setPendingNavPath(null);
          }
        }}
      />
    </ContentArea>
  );
}
